import React, { useState, useEffect, useCallback } from 'react';
import { Maximize2, Download, X, ChevronRight } from 'lucide-react';
import { exportToCSV, exportChartToCSV } from '../../utils/csvExport';

/**
 * Wrapper that adds expand-to-fullscreen + CSV export to any chart/table.
 *
 * Props:
 *  title        – section heading (shown inside expanded modal)
 *  subtitle     – optional description
 *  data         – raw data array (for CSV export and drill-down)
 *  columns      – column defs for DataTable-style data (optional)
 *  chartLabelKey / chartValueKey – for simple chart data CSV export
 *  exportFilename – download filename without extension
 *  onDrillDown  – (item) => ReactNode   optional drill-down renderer
 *  children     – the chart/table to render
 *  expandedChildren – optional alternate children for expanded view (e.g. bigger chart)
 *  noPadding    – if true, remove padding wrapper
 */
const ExpandableVisualization = ({
    title,
    subtitle,
    data = [],
    columns,
    chartLabelKey,
    chartValueKey,
    exportFilename = 'analytics-data',
    onDrillDown,
    children,
    expandedChildren,
    noPadding = false,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [drillDownItem, setDrillDownItem] = useState(null);

    // Escape key closes expanded view
    useEffect(() => {
        if (!expanded) return;
        const handler = (e) => {
            if (e.key === 'Escape') {
                if (drillDownItem) {
                    setDrillDownItem(null);
                } else {
                    setExpanded(false);
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [expanded, drillDownItem]);

    // Lock body scroll when expanded
    useEffect(() => {
        if (expanded) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [expanded]);

    const handleExport = useCallback((e) => {
        e?.stopPropagation();
        if (columns && columns.length && data.length) {
            exportToCSV(data, columns, exportFilename);
        } else if (data.length) {
            exportChartToCSV(data, chartLabelKey || 'label', chartValueKey || 'value', exportFilename);
        }
    }, [data, columns, chartLabelKey, chartValueKey, exportFilename]);

    const handleExpand = useCallback((e) => {
        e?.stopPropagation();
        setExpanded(true);
        setDrillDownItem(null);
    }, []);

    const hasData = data && data.length > 0;

    return (
        <>
            {/* ─── Inline view (inside drawer) ─── */}
            <div className="group relative">
                {/* Toolbar – appears on hover */}
                {hasData && (
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={handleExpand}
                            className="p-1.5 bg-white/90 hover:bg-gray-100 border border-gray-200 rounded-md shadow-sm transition-colors"
                            title="Expand"
                        >
                            <Maximize2 className="w-3.5 h-3.5 text-gray-600" />
                        </button>
                        <button
                            onClick={handleExport}
                            className="p-1.5 bg-white/90 hover:bg-gray-100 border border-gray-200 rounded-md shadow-sm transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-3.5 h-3.5 text-gray-600" />
                        </button>
                    </div>
                )}
                {children}
            </div>

            {/* ─── Expanded modal ─── */}
            {expanded && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => { setExpanded(false); setDrillDownItem(null); }}
                    />

                    {/* Modal */}
                    <div
                        className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scale-in"
                        style={{ width: '90vw', maxWidth: '1200px', height: '85vh', maxHeight: '900px' }}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">{title || 'Expanded View'}</h2>
                                {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleExport}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
                                >
                                    <Download className="w-4 h-4" />
                                    Export CSV
                                </button>
                                <button
                                    onClick={() => { setExpanded(false); setDrillDownItem(null); }}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="flex flex-1 overflow-hidden">
                            {/* Main Visualization */}
                            <div className={`flex-1 overflow-y-auto ${noPadding ? '' : 'p-6'} ${drillDownItem ? 'border-r border-gray-200' : ''}`}>
                                {expandedChildren || children}
                            </div>

                            {/* Drill-Down Side Panel */}
                            {drillDownItem && onDrillDown && (
                                <div className="w-80 flex-shrink-0 overflow-y-auto bg-gray-50 p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-semibold text-gray-800 text-sm">Detail View</h3>
                                        <button
                                            onClick={() => setDrillDownItem(null)}
                                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                                        >
                                            <X className="w-4 h-4 text-gray-500" />
                                        </button>
                                    </div>
                                    {onDrillDown(drillDownItem)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Animation keyframe */}
            <style>{`
                @keyframes scale-in {
                    from { transform: scale(0.95); opacity: 0; }
                    to   { transform: scale(1);    opacity: 1; }
                }
                .animate-scale-in {
                    animation: scale-in 0.2s ease-out;
                }
            `}</style>
        </>
    );
};

export default ExpandableVisualization;
